import { ValueAdapter } from './value.adapter';
import { Model } from 'app/model/backendmodel';
import {
    AdapterRegistry,
    Folder,
    type ITridentResource,
    Parameter,
    PipelinePolicy,
    Tag,
    TriggerSettings,
    NamedAsyncAdapter,
    Constants,
    arrayIsNullOrEmpty,
    IAttribute,
    LinkedService,
    Named
} from 'shell-models-lib';
import { getString } from 'app/shared/utils/string.util';
import { ActivityAsyncAdapter } from './activities/activity.adapter';
import { Metamodel } from 'app/model/base/model';
import { Activity } from 'app/model/pipeline/activity';
import { GenericActivityAdapter } from 'app/model/adapters/activities/generic-activity.adapter';
import { ActivityRegistryService } from 'app/authoring/registries/activity-registry.service';
import { Owned } from 'app/model/base/named';
import { toParameterObject, toParameterClass } from './linkedservices/parameter-config.util';
import { Variable } from '../base/variable';
import { LibraryVariable } from '../base/libraryvariable';
import { GlobalVariables } from 'app/services/globals';
import { differenceWith, isSet, merge } from 'lodash';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import {
    IPipelineCopilotCurrentPipelineContext,
    IPipelineParameterVariableContext,
    PipelineActivityInterimContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { ParameterAdapter } from './parameter.adapter';
import { VariableAdapter } from './variable.adapter';
import { A365WorkspaceCacheService } from 'app/services/metadata-client/A365workspace-cache.service';
import { depWarningDocLink } from 'app/shared/constants/links.const';
import shellapi from 'a365-shell-api';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { calcSapCdcWarning } from 'app/shared/utils/sap-cdc-warning.util';
import { calcSharePointSpnWarning } from 'app/shared/utils/sharepoint-spn-warning.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Pipeline),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            activities: '*',
            parameters: '*',
            annotations: '*',
            variables: '*',
            libraryVariables: '*',
            runDimensions: '*',
            dependencies: '*',
            concurrency: '*',
            policy: '*',
            triggerSettings: '*',
            dataFactoryId: '*' // Preserve dataFactoryId field across save operations
        }
    }
})
export class PipelineAsyncAdapter extends NamedAsyncAdapter<
    Pipeline,
    Model.PipelineResource,
    IPipelineCopilotCurrentPipelineContext
> {
    public async activitiesAndParametersToModel(
        pipeline: Pipeline,
        pipelineProperties: Model.PipelineModel
    ): Promise<void> {
        if (__IS_FABRIC_DPE__) {
            // we should clear model for ADF as well, otherwise activities/parameters/variables are duplicated when refreshing pipeline model
            // keep it for trident for now
            pipeline.activities?.clear();
            pipeline.parameters?.clear();
            pipeline.variables?.clear();
            pipeline.libraryVariables?.clear();
        }

        if (pipelineProperties?.activities) {
            for (const modelActivity of pipelineProperties.activities) {
                if (pipeline) {
                    if (!Metamodel.isModel(modelActivity.type)) {
                        await this._registerActivity(modelActivity.type);
                    }
                    // TS compiler is incapabable of finding that this is assigned and no paths
                    // run to set to undefined. Unnecesary checks adding to code clutter
                    const model = Metamodel.modelByName(modelActivity.type);
                    const adapter = this.context.asyncAdapterFor(model);
                    if (adapter) {
                        pipeline.addActivity(await adapter.toModel(modelActivity));
                    }
                }
            }
            ActivityAsyncAdapter.linkAllActivitiesFromResource(pipelineProperties.activities, pipeline.activities);
        }
        const parameterAdapter = this.context.adapterFor(Parameter.MODEL);

        if (parameterAdapter) {
            if (pipelineProperties?.parameters) {
                for (const parameterName in pipelineProperties.parameters) {
                    pipeline.parameters.add(
                        parameterAdapter.toModel(
                            pipelineProperties.parameters[parameterName],
                            undefined,
                            parameterName,
                            Pipeline.MODEL.name,
                            pipeline.name
                        )
                    );
                    if (
                        !GlobalVariables.strictParamValidation &&
                        !Constants.alphaNumericUnderscoreValidation.test(parameterName)
                    ) {
                        this._logWarning(`Pipeline "${pipeline.name}" with invalid parameter "${parameterName}".`);
                    }
                }
            }
        }
    }

    public override async postToModel(pipeline: Pipeline): Promise<void> {
        await super.postToModel(pipeline);

        let a365WorkspaceCacheService: A365WorkspaceCacheService;
        let linkedServiceRegistryService: LinkedServiceRegistryService;
        if (!GlobalVariables.isCmdApiApp) {
            a365WorkspaceCacheService = await getServiceInstance<A365WorkspaceCacheService>(
                this.context.contextData,
                'A365WorkspaceCacheService'
            );

            linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                this.context.contextData,
                'LinkedServiceRegistryService'
            );
        }

        const attributesFromNamed = Metamodel.modelFor(Named).attributes?.map((attr) => attr.name);
        const visitedEntities = new Set<Owned>();
        visitedEntities.add(pipeline);
        await Promise.all(
            Array.from(pipeline.activities.values()).map(async (activity) => {
                const activityModel = Metamodel.modelOf(activity);
                const adapter = this.context.isAsyncAdapter(activityModel)
                    ? this.context.asyncAdapterFor(activityModel)
                    : this.context.adapterFor(activityModel);
                try {
                    await adapter?.postToModel(activity);
                } catch (e) {
                    this._logError(
                        `Activity "${activity.name}" \`postToModel\` failed in pipeline "${pipeline.name}"`,
                        e as Record<string, string>
                    );
                }

                if (!GlobalVariables.isCmdApiApp) {
                    if (!__IS_A365__ || !a365WorkspaceCacheService.isDEPEnabled || pipeline.showDEPWarning || GlobalVariables.depWarning === false) {
                        return;
                    }

                    const checkIfEntityhasDPEWarning = (entity: Owned): void => {
                        if (pipeline.showDEPWarning || visitedEntities.has(entity)) {
                            return;
                        }
                        visitedEntities.add(entity);

                        let linkedServiceMetadata;
                        if (entity instanceof LinkedService) {
                            try {
                                linkedServiceMetadata = linkedServiceRegistryService.getLinkedServiceConfiguration(Metamodel.modelOf(entity).name);
                            } catch (e) {
                                this._logError(
                                    `Failed to get linked service configuration for "${entity.name}".`,
                                    e as Record<string, string>
                                );
                            }
                        }

                        if (
                            (entity as {showDEPWarning?: boolean}).showDEPWarning ||
                            (entity instanceof LinkedService && entity.integrationRuntime == null && linkedServiceMetadata && !linkedServiceMetadata.noIR)
                        ) {
                            pipeline.showDEPWarning = true;
                            return;
                        }

                        const attributes: IAttribute[] = Metamodel.modelOf(entity).attributes;
                        for (const attribute of attributes) {
                            try {
                                const attributeValue = attribute.value(entity);
                                if (
                                    attributesFromNamed.includes(attribute.name) ||
                                    attribute.name === '_owner' ||
                                    attributeValue == null ||
                                    attribute.templateValue(entity) != null 
                                ) { continue; }
                                if (Array.isArray(attributeValue)) {
                                    if (attributeValue?.length > 0) {
                                        for (const item of attributeValue) {
                                            if (item instanceof Owned) {
                                                checkIfEntityhasDPEWarning(item);
                                            }
                                        }
                                    }
                                } else if (isSet(attributeValue)) {
                                    if (attributeValue?.size > 0) {
                                        for (const item of attributeValue) {
                                            if (item instanceof Owned) {
                                                checkIfEntityhasDPEWarning(item);
                                            }
                                        }
                                    }
                                }else if (attributeValue instanceof Owned) {
                                    checkIfEntityhasDPEWarning(attributeValue as Owned);
                                }
                            } catch(e){}
                        }
                    }
                    checkIfEntityhasDPEWarning(activity);
                }
            })
        );
        if (pipeline.showDEPWarning && !GlobalVariables.isCmdApiApp) {
            const notificationService = await getServiceInstance<{add: (createParameter: shellapi.notifications.INotificationCreateParameter, showToast: boolean, avoidDuplication: boolean) => number}>(
                this.context.contextData,
                'NotificationService'
            );
            notificationService.add({
                type: 'Pipeline',
                title: stringResources.SynapseDEPError_Title,
                subTitle: stringResources.SynapseDEPError,
                actionLinkTitle: stringResources.LearnMore,
                actionLinkAction: () => {
                    window.open(depWarningDocLink, '_blank');
                },
                status: 'Warning'
            }, true, true);
        }
        pipeline.calcLegacyDependencies();
        if (!__IS_FABRIC_DPE__) {
            calcSapCdcWarning(pipeline);
            calcSharePointSpnWarning(pipeline);
        }
    }

    public override async toFabricCopilotContext(pipeline: Pipeline): Promise<IPipelineCopilotCurrentPipelineContext> {
        if (pipeline) {
            const pipelineContext: IPipelineCopilotCurrentPipelineContext = {
                Parameters: [],
                Variables: [],
                Activities: []
            };
            if (pipeline.activities?.size > 0) {
                const activityPromises: Array<Promise<PipelineActivityInterimContext>> = Array.from(pipeline.activities).map(
                    async (activity) => {
                        const adapter = this.context.asyncAdapterFor<
                            Activity,
                            Model.Activity,
                            PipelineActivityInterimContext
                        >(Metamodel.modelOf(activity));
                        return adapter.toFabricCopilotContext(activity);
                    }
                );

                const activityInterimContexts = (await Promise.all(activityPromises)).filter(
                    (context): context is PipelineActivityInterimContext => !!context
                );

                if (activityInterimContexts.length > 0) {
                    pipelineContext.Activities = activityInterimContexts;
                }
            }

            if (pipeline.parameters.size > 0) {
                const parameterAdapter = this.context.adapterFor<
                    Parameter,
                    Model.ParameterSpecification,
                    IPipelineParameterVariableContext
                >(Parameter.MODEL);
                if (parameterAdapter) {
                    const parameters = Array.from(pipeline.parameters.filter((item) => !!item.name)).map((parameter) =>
                        parameterAdapter.toFabricCopilotContext(parameter)
                    );

                    if (parameters.length > 0) {
                        pipelineContext.Parameters = parameters;
                    }
                }
            }

            if (pipeline.variables?.size > 0) {
                const variableAdapter = this.context.adapterFor<
                    Variable,
                    Model.ParameterSpecification,
                    IPipelineParameterVariableContext
                >(Variable.MODEL);
                if (variableAdapter) {
                    const variables = Array.from(pipeline.variables.filter((item) => !!item.name)).map((variable) =>
                        variableAdapter.toFabricCopilotContext(variable)
                    );

                    if (variables.length > 0) {
                        pipelineContext.Variables = variables;
                    }
                }
            }
            return pipelineContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IPipelineCopilotCurrentPipelineContext,
        pipeline?: Pipeline
    ): Promise<Pipeline> {
        if (!pipeline) {
            pipeline = Pipeline.MODEL.create<Pipeline>();
        }
        // 1. handle activities
        if (!arrayIsNullOrEmpty(context.Activities)) {
            const activitiesToRemove: Activity[] = [];
            if (pipeline.activities.size > 0) {
                for (const existingActivity of pipeline.activities) {
                    // existingActivity activity is not in the latest context, should remove from pipeline.activities
                    if (
                        existingActivity &&
                        context.Activities.find((activityContext) => {
                            const activityModelType =
                                activityContext.Type === 'Unknown'
                                    ? activityContext.OriginalActivityType
                                    : activityContext.Type;
                            return (
                                existingActivity.name === activityContext.Name &&
                                Metamodel.modelOf(existingActivity).name === activityModelType
                            );
                        }) == null
                    ) {
                        activitiesToRemove.push(existingActivity);
                    }
                }
                activitiesToRemove.forEach((activity) => {
                    pipeline.activities.delete(activity);
                });
            }
            const allExistingActivities = Array.from(pipeline.activities);

            for (const activityContext of context.Activities) {
                // support update known and unknown activity
                const activityModelType =
                    activityContext.Type === 'Unknown' ? activityContext.OriginalActivityType : activityContext.Type;
                let activity = allExistingActivities.find(
                    (activity) =>
                        activity.name === activityContext.Name && Metamodel.modelOf(activity).name === activityModelType
                );
                const shouldAdd = !activity;
                const activityModel = Metamodel.isModel(activityModelType)
                    ? Metamodel.modelByName(activityModelType)
                    : undefined;
                const adapter = this.context.asyncAdapterFor<Activity, Model.Activity, PipelineActivityInterimContext>(
                    activityModel
                );
                if (adapter) {
                    activity = await adapter.fromFabricCopilotContext(activityContext, activity);
                    if (activity && shouldAdd) {
                        pipeline.addActivity(activity);
                    }
                }
            }
            ActivityAsyncAdapter.linkAllActivitiesFromFabricContext(context.Activities, pipeline.activities);
        } else {
            pipeline.activities.clear();
        }

        // 2. handle parameters
        if (!arrayIsNullOrEmpty(context.Parameters)) {
            const parameterAdapter = this.context.adapterFor<
                Parameter,
                Model.ParameterSpecification,
                IPipelineParameterVariableContext
            >(Parameter.MODEL);
            for (const parameter of context.Parameters) {
                let parameterModel = pipeline.parameters.find((item) => item.name === parameter.Name);
                const shouldAdd = !parameterModel;
                parameterModel = parameterAdapter.fromFabricCopilotContext(parameter, parameterModel);
                if (shouldAdd) {
                    pipeline.parameters.add(parameterModel);
                }
            }
            const parametersToRemove: Parameter[] = differenceWith(
                Array.from(pipeline.parameters.values()),
                context.Parameters,
                (paramModel, paramContext) => paramModel.name === paramContext.Name && paramModel._type === ParameterAdapter.getParameterTypeFromFabricContext(paramContext.Type)
            );
            parametersToRemove.forEach((param) => {
                pipeline.parameters.delete(param);
            });

        } else {
            pipeline.parameters.clear();
        }

        // 3. handle variables
        if (!arrayIsNullOrEmpty(context.Variables)) {
            const variableAdapter = this.context.adapterFor<
                Variable,
                Model.ParameterSpecification,
                IPipelineParameterVariableContext
            >(Variable.MODEL);
            for (const variable of context.Variables) {
                let variableModel = pipeline.variables.find((item) => item.name === variable.Name);
                const shouldAdd = !variableModel;
                variableModel = variableAdapter.fromFabricCopilotContext(variable, variableModel);
                if (shouldAdd) {
                    pipeline.variables.add(variableModel);
                }
            }
            const variablesToRemove: Variable[] = differenceWith(
                Array.from(pipeline.variables.values()),
                context.Variables,
                (varModel, varContext) => varModel.name === varContext.Name && varModel._type === VariableAdapter.getTypeFromFabricContext(varContext.Type)
            );
            variablesToRemove.forEach((varItem) => {
                pipeline.variables.delete(varItem);
            });

        } else {
            pipeline.variables.clear();
        }
        return pipeline;
    }

    protected async _toResource(pipeline: Pipeline): Promise<Model.PipelineResource> {
        const pipelineResource = (await super._toResource(pipeline)) as Model.PipelineResource;

        const activities: Model.Activity[] = [];
        for (const activity of pipeline.activities) {
            const model = Metamodel.modelOf(activity);
            const adapter = this.context.asyncAdapterFor(model);
            if (adapter) {
                const activityResource = await adapter.toResource(activity);
                activities.push(activityResource);
            }
        }

        if (activities.length > 0 || __IS_FABRIC_DPE__) {
            this.properties(pipelineResource).activities = activities;
        }

        if (pipeline.concurrency) {
            this.properties(pipelineResource).concurrency = pipeline.concurrency;
        }

        const policyAdapter = this.context.adapterFor(PipelinePolicy.MODEL);
        if (policyAdapter && pipeline.policy && !__IS_FABRIC_DPE__) {
            this.properties(pipelineResource).policy = policyAdapter.toResource(pipeline.policy);
        }

        const parameters: { [propertyName: string]: Model.ParameterSpecification } = {};
        const parameterAdapter = this.context.adapterFor(Parameter.MODEL);
        if (parameterAdapter) {
            pipeline.parameters.forEach((parameter) => {
                if (parameter.name) {
                    parameters[getString(parameter.name)] = parameterAdapter.toResource(parameter);
                }
            });
        }

        if (pipeline.parameters.size > 0) {
            this.properties(pipelineResource).parameters = parameters;
        }

        const variables: { [propertyName: string]: Model.VariableSpecification } = {};
        const variableAdapter = this.context.adapterFor(Variable.MODEL);
        if (variableAdapter) {
            pipeline.variables.forEach((variable) => {
                if (variable.name) {
                    variables[getString(variable.name)] = variableAdapter.toResource(variable);
                }
            });
        }

        if (pipeline.variables.size > 0) {
            this.properties(pipelineResource).variables = variables;
        }

        const wkspcVariables: { [propertyName: string]: Model.LibraryVariableSpecification } = {};
        const wkspcVariableAdapter = this.context.adapterFor(LibraryVariable.MODEL);
        if (wkspcVariableAdapter) {
            pipeline.libraryVariables.forEach((wkspcVar) => {
                if (wkspcVar.variableName && wkspcVar.libraryName) {
                    wkspcVariables[wkspcVar.name] = wkspcVariableAdapter.toResource(wkspcVar);
                }
            });
        }

        if (pipeline.libraryVariables.size > 0) {
            this.properties(pipelineResource).libraryVariables = wkspcVariables;
        }

        if (pipeline.runDimensions.length > 0) {
            this.properties(pipelineResource).runDimensions = toParameterObject(pipeline.runDimensions);
        }

        if (GlobalVariables.enableRunConditions) {
            const dependencies = pipeline.dependencies as Model.Dependency[];
            if (dependencies.length > 0) {
                this.properties(pipelineResource).dependencies = dependencies;
            }
        }

        if (GlobalVariables.enableRunConditions && pipeline.dependencyConnectionId) {
            this.properties(pipelineResource).dependencyConnectionId = pipeline.dependencyConnectionId;
        }

        this.properties(pipelineResource).folder = this.context.adapterFor(Folder.MODEL).toResource(pipeline.folder);
        if (!__IS_FABRIC_DPE__) {
            // Annotations do not exist in Fabric (Trident).
            this.properties(pipelineResource).annotations = [];
            const tagAdapter = this.context.adapterFor(Tag.MODEL);
            for (const tagNamed of pipeline.tags) {
                // do not save it if user created an empty tag
                const tagResource = tagAdapter.toResource(tagNamed);
                if (tagResource) {
                    this.properties(pipelineResource).annotations.push(tagResource);
                }
            }
            for (const tagNamed of pipeline.malformedTags) {
                const tagResource = tagAdapter.toResource(tagNamed);
                if (tagResource) {
                    this.properties(pipelineResource).annotations.push(tagResource);
                }
            }
        }

        if (__IS_FABRIC_DPE__ && pipeline?.triggerSettings?.typeProperties) {
            const triggerSettingsAdapter = this.context.asyncAdapterFor(TriggerSettings.MODEL);
            this.properties(pipelineResource).triggerSettings = await triggerSettingsAdapter.toResource(
                pipeline.triggerSettings
            );
        }

        if (pipeline.dataFactoryId) {
            this.properties(pipelineResource).dataFactoryId = pipeline.dataFactoryId;
        }

        if (pipeline.raw) {
            const parsed = JSON.parse(pipeline.raw);
            merge(pipelineResource, parsed);
        }
        return pipelineResource;
    }

    protected async _toModel(modelPipelineResource: Model.PipelineResource, pipeline?: Pipeline): Promise<Pipeline> {
        // TODO: This has to be a merge retaining activity instances in the pipeline.
        if (!pipeline) {
            pipeline = Pipeline.MODEL.create<Pipeline>();
        }

        await super._toModel(modelPipelineResource, pipeline);

        // Explicitly preserve dataFactoryId
        if (modelPipelineResource.properties?.dataFactoryId) {
            pipeline.dataFactoryId = modelPipelineResource.properties.dataFactoryId;
        }

        await this.activitiesAndParametersToModel(pipeline, modelPipelineResource.properties);

        if (modelPipelineResource.properties?.concurrency) {
            pipeline.concurrency = modelPipelineResource.properties.concurrency;
        }

        const policyAdapter = this.context.adapterFor(PipelinePolicy.MODEL);
        const enableElapsedTimeMetric =
            !__IS_FABRIC_DPE__ && modelPipelineResource.properties?.policy?.elapsedTimeMetric?.duration;
        if (policyAdapter && modelPipelineResource.properties?.policy && enableElapsedTimeMetric) {
            pipeline.policy = policyAdapter.toModel(modelPipelineResource.properties.policy);
        }

        const variableAdapter = this.context.adapterFor(Variable.MODEL);

        if (variableAdapter) {
            if (modelPipelineResource.properties?.variables) {
                for (const variableName in modelPipelineResource.properties.variables) {
                    pipeline.variables.add(
                        variableAdapter.toModel(
                            modelPipelineResource.properties.variables[variableName],
                            undefined,
                            variableName,
                            Variable.MODEL.name,
                            pipeline.name
                        )
                    );
                }
            }
        }

        const wkspcVariableAdapter = this.context.adapterFor(LibraryVariable.MODEL);
        if (wkspcVariableAdapter) {
            if (modelPipelineResource.properties?.libraryVariables) {
                for (const key in modelPipelineResource.properties.libraryVariables) {
                    const resourceVariable: Model.LibraryVariableSpecification =
                        modelPipelineResource.properties.libraryVariables[key];
                    const modelVariable: LibraryVariable = wkspcVariableAdapter.toModel(
                        resourceVariable,
                        undefined,
                        resourceVariable.variableName,
                        LibraryVariable.MODEL.name,
                        pipeline.name
                    );
                    modelVariable.name = key;
                    pipeline.libraryVariables.add(modelVariable);
                }
            }
        }

        if (modelPipelineResource.properties?.runDimensions) {
            pipeline.runDimensions = toParameterClass(modelPipelineResource.properties.runDimensions, pipeline);
        }

        if (GlobalVariables.enableRunConditions) {
            pipeline.dependencies = modelPipelineResource.properties?.dependencies ?? [];
        }

        if (GlobalVariables.enableRunConditions &&modelPipelineResource.properties?.dependencyConnectionId) {
            pipeline.dependencyConnectionId = modelPipelineResource.properties.dependencyConnectionId;
        }

        if (modelPipelineResource.properties) {
            pipeline.folder = Owned.create<Folder>(Folder.MODEL, pipeline);
            this.context.adapterFor(Folder.MODEL).toModel(modelPipelineResource.properties.folder, pipeline.folder);
            if (modelPipelineResource.properties.annotations) {
                const tagAdapter = this.context.adapterFor(Tag.MODEL);
                for (const tagNamed of modelPipelineResource.properties.annotations) {
                    if (typeof tagNamed === 'string') {
                        pipeline.tags.add(tagAdapter.toModel(tagNamed));
                    } else {
                        pipeline.malformedTags.add(tagAdapter.toModel(tagNamed));
                    }
                }
            }
            pipeline.lastModifiedByObjectId = modelPipelineResource.properties.lastModifiedByObjectId;
            if (modelPipelineResource.properties.extraTridentInfo) {
                pipeline.lastModifiedByPrincipalName = pipeline.extraTridentInfo?.modifiedByUser?.userPrincipalName;
            }
        }

        if (__IS_FABRIC_DPE__ && modelPipelineResource?.properties?.triggerSettings) {
            const triggerSettings = modelPipelineResource.properties.triggerSettings;
            pipeline.triggerSettings = Owned.create(TriggerSettings.MODEL, pipeline);
            await this.context
                .asyncAdapterFor(TriggerSettings.MODEL)
                .toModel(triggerSettings, pipeline.triggerSettings);
        }

        pipeline.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelPipelineResource, Pipeline.MODEL)
        );

        return pipeline;
    }

    protected override async _toAzureResource(tridentResource: ITridentResource): Promise<Model.PipelineResource> {
        const name = tridentResource.displayName;
        let properties: Model.PipelineModel & Partial<Record<'extraTridentInfo', string>>;
        let id: string;
        try {
            const workloadPayload = JSON.parse(tridentResource.workloadPayload);
            const idParts = workloadPayload?.id?.split('/');
            idParts[6] = 'Microsoft.DataFactory';
            idParts[7] = 'factories';
            idParts[idParts.length - 1] = name;
            id = idParts.join('/');
            properties = workloadPayload.properties;
        } catch (e) {
            id = `/subscriptions/xxxxxxxxx/resourceGroups/xxxx/providers/Microsoft.DataFactory/factories/xxxxxxx/pipelines/${name}`;
            properties = {};
        }
        properties.description = tridentResource.description;
        const azureResource = {
            name,
            id,
            objectId: tridentResource.objectId,
            workspaceObjectId: tridentResource.workspaceObjectId,
            properties
        };
        const extraInfo: Record<string, unknown> = {};
        Object.keys(tridentResource).forEach((key) => {
            const typedKey = key as keyof ITridentResource;
            if (
                ![
                    'description',
                    'displayName',
                    'objectId',
                    'workloadPayload',
                    'payloadContentType',
                    'workspaceObjectId'
                ].includes(typedKey)
            ) {
                extraInfo[typedKey] = tridentResource[typedKey];
            }
        });
        azureResource.properties.extraTridentInfo = JSON.stringify(extraInfo);
        return azureResource;
    }

    private async _registerActivity(activityType: string): Promise<void> {
        const model = Metamodel.createModel(Activity.MODEL, {
            name: activityType,
            display: [activityType],
            abstract: false,
            createdFromUnknownType: true
        });
        AdapterRegistry.createAdapter(
            {
                model,
                asynchronous: true,
                adapts: {
                    name: '*',
                    type: '*',
                    description: '*',
                    dependsOn: '*'
                }
            },
            GenericActivityAdapter
        );
        const activityRegistryService = await getServiceInstance<ActivityRegistryService>(
            this.context.contextData,
            'ActivityRegistryService'
        );
        activityRegistryService.registerActivityConfiguration({
            label: activityType,
            name: activityType,
            activityType: model,
            iconClass: 'svg-i_data_flow_16x16',
            notShowInTypeSelection: true, // unknown type shouldn't support create for security reason
            iconTransform: ''
        });
    }

}
